import { type CatchUpItem, dayMonth, useCatchUpMark, useCatchUpPlan } from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GRAD,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  grad,
  pillClass,
} from '../design/forms';
import {
  ArrowCounterClockwiseIcon,
  ArrowUpRightIcon,
  CaretDownIcon,
  CheckIcon,
  ExamIcon,
} from '../design/icons';

/*
 * «Догнати пропущене» in «Форми 2» (28.10): each missed lesson a soft card in its subject's colour with
 * the subject's picture big and quiet behind (as the tasks of «Домашні»): the day and why as a soft mark,
 * «Спершу» on the one a test is coming for, the subject and the topic, what to do as pills ↗ (the
 * homework, the materials, the boards) and the test as a mark; «Готово» a dark pill. A lesson caught up
 * goes quiet on the paper with «Повернути».
 */

const SHOWN = 4;

function Item({
  item,
  first,
  onToggle,
}: {
  item: CatchUpItem;
  first: boolean;
  /** the student ticks it off; the family only looks */
  onToggle?: (done: boolean) => void;
}) {
  const { t } = useTranslation();
  const look = subjectLook(item.subject);
  const tone = SUBJECT_GRAD[look.tone];
  const mark =
    'inline-flex h-6 items-center gap-1 rounded-[7px] px-2 text-[12px] leading-none font-medium';
  return (
    <li
      className="relative isolate flex min-w-0 flex-col gap-3 overflow-hidden rounded-[22px] p-4"
      style={
        item.done
          ? { backgroundColor: PAPER, color: INK_SOFT }
          : { backgroundImage: grad(tone), color: GRAD[tone].ink }
      }
    >
      {!item.done && (
        <SubjectArt
          icon={look.icon}
          tone={tone}
          size={104}
          ghost
          className="pointer-events-none absolute -top-5 -right-4 -z-10"
        />
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {first && item.test && !item.done && (
          <span className={mark} style={{ backgroundImage: grad('orange'), color: '#FFFFFF' }}>
            {t('catchUp.first')}
          </span>
        )}
        <span
          className={mark}
          style={{ backgroundColor: item.done ? '#FFFFFF' : 'rgb(255 255 255 / 0.8)', color: INK }}
        >
          {dayMonth(item.date)} · {t(`catchUp.marks.${item.mark}`)}
        </span>
      </div>
      <Link
        to="/lesson-pack/$lessonId"
        params={{ lessonId: item.lessonId }}
        // clear of the picture in the corner: it never comes near what is pressed
        className="group block max-w-[calc(100%-84px)] self-start"
      >
        <span
          className={`block font-display text-[22px] leading-[27px] font-light tracking-[-0.02em] group-hover:underline ${item.done ? 'line-through' : ''}`}
          style={{ color: item.done ? INK_FAINT : INK }}
        >
          {item.subject}
        </span>
        <span className="mt-0.5 block text-[14px] leading-5" style={{ color: INK_SOFT }}>
          {item.topic ?? t('catchUp.noTopic')}
        </span>
      </Link>
      {!item.done && (item.test || item.homework || item.materials > 0 || item.boards > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {item.test && (
            <span
              className="inline-flex min-h-9 items-center gap-1.5 rounded-[10px] bg-white/80 px-3 py-1.5 text-[13px] font-medium"
              style={{ color: GRAD.coral.ink }}
            >
              <ExamIcon size={15} weight="fill" aria-hidden className="shrink-0" />
              {t('catchUp.test', { title: item.test.title, date: dayMonth(item.test.dueOn) })}
            </span>
          )}
          {item.homework && (
            <Link to="/homework" className={`${pillClass('light', 'sm')} max-w-full`}>
              <span className="truncate">
                {t('catchUp.homework', {
                  title: item.homework.title,
                  date: dayMonth(item.homework.dueOn),
                })}
                {item.homework.done && ` · ${t('catchUp.submitted')}`}
              </span>
              {item.homework.done ? (
                <span
                  className="flex size-7 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundImage: grad('mint'), color: GRAD.mint.ink }}
                >
                  <CheckIcon size={12} weight="bold" aria-hidden />
                </span>
              ) : (
                <PillIcon icon={ArrowUpRightIcon} size="sm" />
              )}
            </Link>
          )}
          {item.materials > 0 && (
            <Link to="/materials" className={pillClass('light', 'sm')}>
              {t('catchUp.materials', { count: item.materials })}
              <PillIcon icon={ArrowUpRightIcon} size="sm" />
            </Link>
          )}
          {item.boards > 0 && (
            <Link to="/materials" className={pillClass('light', 'sm')}>
              {t('catchUp.boards', { count: item.boards })}
              <PillIcon icon={ArrowUpRightIcon} size="sm" />
            </Link>
          )}
        </div>
      )}
      {onToggle && (
        <button
          type="button"
          onClick={() => onToggle(!item.done)}
          className={`${pillClass(item.done ? 'light' : 'dark')} mt-auto self-start`}
        >
          {item.done ? t('catchUp.undo') : t('catchUp.done')}
          <PillIcon icon={item.done ? ArrowCounterClockwiseIcon : CheckIcon} />
        </button>
      )}
    </li>
  );
}

/**
 * «Догнати пропущене»: the missed lessons of the last weeks, what to catch up first (a test is
 * coming), with homework, materials and boards. Hidden when nothing was missed.
 */
export function CatchUp({
  schoolId,
  studentId,
  canTick,
  className = '',
  bare = false,
}: {
  schoolId: string;
  studentId: string;
  canTick: boolean;
  className?: string;
  /** on its own page: the page's title says what it is, the block shows only the lessons */
  bare?: boolean;
}) {
  const { t } = useTranslation();
  const plan = useCatchUpPlan(studentId);
  const mark = useCatchUpMark();
  const [all, setAll] = useState(false);
  const items = plan.data ?? [];
  if (items.length === 0) return null;
  const left = items.filter((i) => !i.done).length;
  const shown = all ? items : items.slice(0, SHOWN);
  return (
    <section className={`flex min-w-0 flex-col rounded-[28px] bg-white p-5 md:p-6 ${className}`}>
      {!bare && (
        <>
          <Head
            title={t('catchUp.title')}
            note={left === 0 ? t('catchUp.allDone') : t('catchUp.left', { count: left })}
          />
          <p className="-mt-3 mb-4 text-[14px]" style={{ color: INK_SOFT }}>
            {t('catchUp.hint')}
          </p>
        </>
      )}
      <ul className="grid grid-cols-1 gap-2 xl:grid-cols-2">
        {shown.map((item, i) => (
          <Item
            key={item.lessonId}
            item={item}
            first={i === 0}
            {...(canTick && {
              onToggle: (done: boolean) =>
                mark.mutate({ schoolId, studentId, lessonId: item.lessonId, done }),
            })}
          />
        ))}
      </ul>
      {items.length > SHOWN && (
        <button
          type="button"
          aria-expanded={all}
          onClick={() => setAll(!all)}
          className="mt-4 flex items-center gap-2 self-start text-[15px] font-medium"
          style={{ color: INK }}
        >
          {all ? t('catchUp.less') : t('catchUp.more')}
          <span className="text-[13px] font-normal" style={{ color: INK_FAINT }}>
            {items.length}
          </span>
          <CaretDownIcon
            size={12}
            weight="bold"
            aria-hidden
            className={`transition-transform duration-300 ${all ? 'rotate-180' : ''}`}
          />
        </button>
      )}
    </section>
  );
}
